import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { CheckCircle, XCircle, FileText, Clock, AlertCircle, Pen } from "lucide-react";

const QuoteViewPage = () => {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      const res = await axios.get(`${API}/quotes/public/${quoteId}`);
      setQuote(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Quote not found");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/quotes/public/${quoteId}/approve`);
      toast.success("Quote approved!");
      fetchQuote();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve");
    }
  };

  const handleReject = async () => {
    if (!window.confirm("Are you sure you want to reject this quote?")) return;
    try {
      await axios.post(`${API}/quotes/public/${quoteId}/reject`);
      toast.success("Quote rejected");
      fetchQuote();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    }
  };

  const startSign = () => {
    setSigning(true);
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = canvas.offsetWidth;
        canvas.height = 150;
        const ctx = canvas.getContext("2d");
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
      }
    }, 100);
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const onMouseMove = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const onMouseUp = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const submitSignature = async () => {
    const canvas = canvasRef.current;
    const data = canvas.toDataURL("image/png");
    try {
      await axios.post(`${API}/quotes/public/${quoteId}/sign`, { signature_data: data });
      toast.success("Quote signed successfully!");
      setSigning(false);
      fetchQuote();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sign");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-4">
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-8 text-center max-w-md">
          <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">Quote Not Available</h2>
          <p className="text-[#A0AAB2]">{error}</p>
        </div>
      </div>
    );
  }

  if (!quote) return null;

  const statusConfig = {
    sent: { label: "Awaiting Your Approval", color: "bg-blue-500/20 text-blue-400", icon: Clock },
    viewed: { label: "Under Review", color: "bg-blue-500/20 text-blue-400", icon: Clock },
    approved: { label: "Approved", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
    signed: { label: "Signed", color: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle },
    rejected: { label: "Rejected", color: "bg-red-500/20 text-red-400", icon: XCircle },
    invoiced: { label: "Invoiced", color: "bg-purple-500/20 text-purple-400", icon: FileText },
  };

  const st = statusConfig[quote.status] || { label: quote.status, color: "bg-gray-500/20 text-gray-400", icon: Clock };
  const StatusIcon = st.icon;

  return (
    <div className="min-h-screen bg-[#0B0C10] py-8 px-4" data-testid="quote-view-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-white">{quote.title}</h1>
              <p className="text-[#A0AAB2] text-sm">{quote.quote_number}</p>
            </div>
            <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full ${st.color}`}>
              <StatusIcon size={16} />
              <span className="text-sm font-medium">{st.label}</span>
            </div>
          </div>
          <div className="flex items-center space-x-4 text-[#A0AAB2] text-sm">
            <span>From: <strong className="text-white">New Leaf PMC LLC</strong></span>
            <span>To: <strong className="text-white">{quote.client_name}</strong></span>
          </div>
          {quote.description && <p className="text-[#A0AAB2] text-sm mt-3">{quote.description}</p>}
        </div>

        {/* Line Items */}
        <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6 mb-4">
          <h3 className="text-white font-semibold mb-3">Items</h3>
          <div className="space-y-2">
            {quote.line_items?.map((item, i) => (
              <div key={i} className="flex items-center justify-between bg-[#1F2027] rounded-lg p-3">
                <span className="text-white text-sm">{item.description}</span>
                <span className="text-[#FF6B35] font-bold">${item.price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#2A2B32] mt-4 pt-4">
            <div className="flex items-center justify-between">
              <span className="text-white text-lg font-bold">Total</span>
              <span className="text-[#FF6B35] text-2xl font-bold" data-testid="quote-total">
                ${quote.total_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {quote.notes && (
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6 mb-4">
            <h3 className="text-white font-semibold mb-2">Notes</h3>
            <p className="text-[#A0AAB2] text-sm whitespace-pre-wrap">{quote.notes}</p>
          </div>
        )}

        {/* Signature (if signed) */}
        {quote.signature_data && (
          <div className="bg-[#15161C] border border-green-500/30 rounded-xl p-6 mb-4">
            <h3 className="text-green-400 font-semibold mb-2">Signature</h3>
            <img src={quote.signature_data} alt="Signature" className="max-h-24 bg-white rounded p-2" />
            <p className="text-[#A0AAB2] text-xs mt-2">Signed on {new Date(quote.signed_at).toLocaleDateString()}</p>
          </div>
        )}

        {/* Action Buttons */}
        {(quote.status === "sent" || quote.status === "viewed") && (
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6 mb-4">
            <h3 className="text-white font-semibold mb-4">Approve or Reject This Quote</h3>
            <div className="flex space-x-3">
              <button onClick={handleApprove} data-testid="approve-quote-btn"
                className="flex-1 flex items-center justify-center space-x-2 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors">
                <CheckCircle size={18} /><span>Approve</span>
              </button>
              <button onClick={handleReject} data-testid="reject-quote-btn"
                className="flex-1 flex items-center justify-center space-x-2 py-3 bg-red-500/20 text-red-400 rounded-lg font-semibold hover:bg-red-500/30 transition-colors">
                <XCircle size={18} /><span>Reject</span>
              </button>
            </div>
          </div>
        )}

        {/* Sign Section */}
        {quote.status === "approved" && !signing && (
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6 mb-4">
            <h3 className="text-white font-semibold mb-2">Sign to Confirm</h3>
            <p className="text-[#A0AAB2] text-sm mb-4">Click below to add your signature and confirm this quote.</p>
            <button onClick={startSign} data-testid="start-sign-btn"
              className="flex items-center space-x-2 px-6 py-3 gradient-btn rounded-lg font-semibold">
              <Pen size={18} /><span>Sign Quote</span>
            </button>
          </div>
        )}

        {signing && (
          <div className="bg-[#15161C] border border-[#FF6B35]/50 rounded-xl p-6 mb-4">
            <h3 className="text-white font-semibold mb-3">Draw Your Signature</h3>
            <canvas ref={canvasRef} data-testid="signature-canvas"
              className="w-full h-[150px] bg-white rounded-lg cursor-crosshair"
              onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
              onTouchStart={onMouseDown} onTouchMove={onMouseMove} onTouchEnd={onMouseUp} />
            <div className="flex space-x-3 mt-4">
              <button onClick={clearSignature} className="flex-1 py-3 bg-[#2A2B32] text-white rounded-lg">Clear</button>
              <button onClick={submitSignature} data-testid="submit-signature-btn"
                className="flex-1 py-3 gradient-btn rounded-lg font-semibold">Submit Signature</button>
            </div>
          </div>
        )}

        {/* Thank you for signed quotes */}
        {(quote.status === "signed" || quote.status === "invoiced") && (
          <div className="bg-[#15161C] border border-green-500/30 rounded-xl p-6 text-center">
            <CheckCircle size={40} className="text-green-400 mx-auto mb-3" />
            <h3 className="text-white font-bold text-lg mb-2">Quote Confirmed</h3>
            <p className="text-[#A0AAB2]">Thank you! We'll be in touch to schedule your project.</p>
            <p className="text-[#A0AAB2] text-sm mt-2">Call us: <a href="tel:863-340-9244" className="text-[#FF6B35]">863-340-9244</a></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteViewPage;
